import React from 'react';
import { observer } from 'mobx-react';
import type { SkillDto } from '@services/skillService';
import { ImgMagic } from '@components/Images/ImgMagic';

type SkillMentionMenuProps = {
  /** Already filtered + sorted list of matching skills. */
  matches: SkillDto[];
  /** Whether the user has any skills defined at all (controls the empty-state message). */
  hasAnySkills: boolean;
  highlightedIndex: number;
  onSelect: (skill: SkillDto) => void;
  onHoverIndex: (index: number) => void;
};

/** Floating "/" slash-command popup listing skills to apply to the active chat. */
const SkillMentionMenu: React.FC<SkillMentionMenuProps> = observer(({ matches, hasAnySkills, highlightedIndex, onSelect, onHoverIndex }) => {
  return (
    <div className="slash-skill-menu" role="listbox">
      {matches.length === 0 ? (
        <div className="slash-skill-menu-empty">
          {hasAnySkills ? 'No skills match' : 'No skills yet — create one in Settings → Skills'}
        </div>
      ) : (
        <div className="slash-skill-menu-items">
          {matches.map((skill, index) => (
            <div
              key={skill.id}
              role="option"
              aria-selected={index === highlightedIndex}
              className={'slash-skill-menu-item' + (index === highlightedIndex ? ' slash-skill-menu-item--active' : '')}
              onMouseDown={(e) => { e.preventDefault(); onSelect(skill); }}
              onMouseEnter={() => onHoverIndex(index)}
            >
              <span className="slash-skill-menu-icon"><ImgMagic /></span>
              <div className="slash-skill-menu-info">
                <span className="slash-skill-menu-name">{skill.name}</span>
                {skill.description && <span className="slash-skill-menu-desc">{skill.description}</span>}
              </div>
              <span className="slash-skill-menu-tools">{skill.tools.length === 0 ? 'All tools' : `${skill.tools.length} tool${skill.tools.length === 1 ? '' : 's'}`}</span>
            </div>
          ))}
        </div>
      )}
      <div className="slash-skill-menu-footer">
        <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
        <span><kbd>Enter</kbd> select</span>
        <span><kbd>Esc</kbd> close</span>
      </div>
    </div>
  );
});

export default SkillMentionMenu;
